import { useState, useEffect } from 'react';
import { parseApiError } from '@/utils/api-errors';
import Modal from '../common/Modal';
import FaIcon from '../common/FaIcon';
import Button from '../common/Button';
import type { UserCurrentPosition, ICSPosition, ESF, QualifiedPosition, PositionAssignment } from '@/types/position';
import { useICSPositions, useESFs, useQualifiedPositions, useIncidentAssignments, useSwitchPosition, useSelfAssignPosition } from '@/hooks/usePositions';
import { useRelieveAssignment } from '@/hooks/usePositions';
import { toast } from '@/utils/toast';
import type { AxiosError } from 'axios';

interface SwitchPositionModalProps {
  isOpen: boolean;
  onClose: () => void;
  incidentId: string;
  currentPosition?: UserCurrentPosition | null;
}

type ConfirmAction = {
  type: 'ics';
  id: string;
  name: string;
} | {
  type: 'esf';
  id: string;
  name: string;
};

function getErrorMessage(error: unknown, fallback: string): string {
  const axiosErr = error as AxiosError<{ message?: string }>;
  // The backend envelope is { error: { message } }; data.message was always
  // undefined, so this helper only ever returned its fallback.
  return parseApiError(axiosErr).message || fallback;
}

export default function SwitchPositionModal({ isOpen, onClose, incidentId, currentPosition }: SwitchPositionModalProps) {
  const [confirmTarget, setConfirmTarget] = useState<ConfirmAction | null>(null);

  const { data: icsData, isLoading: icsLoading } = useICSPositions();
  const { data: esfData, isLoading: esfLoading } = useESFs();
  const { data: qualifiedData } = useQualifiedPositions(incidentId);
  const { data: assignmentsData } = useIncidentAssignments(incidentId);
  const switchMutation = useSwitchPosition();
  const selfAssignMutation = useSelfAssignPosition();
  const relieveMutation = useRelieveAssignment();

  const icsPositions: ICSPosition[] = icsData || [];
  const esfs: ESF[] = esfData || [];
  const qualifiedPositions: QualifiedPosition[] = qualifiedData || [];
  const assignments: PositionAssignment[] = assignmentsData || [];

  const qualifiedIcsIds = new Set(qualifiedPositions.filter(q => q.type === 'ics').map(q => q.id));
  const qualifiedEsfIds = new Set(qualifiedPositions.filter(q => q.type === 'esf').map(q => q.id));

  // Build maps of filled positions -> assignee name
  const filledIcsMap = new Map<string, string>();
  const filledEsfMap = new Map<string, string>();
  for (const a of assignments) {
    if (a.is_current) {
      const name = a.user?.name || a.contact_name || 'Assigned';
      if (a.ics_position_id) filledIcsMap.set(a.ics_position_id, name);
      if (a.esf_id) filledEsfMap.set(a.esf_id, name);
    }
  }

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (confirmTarget) {
          setConfirmTarget(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose, confirmTarget]);

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setConfirmTarget(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isLoading = icsLoading || esfLoading;
  const isMutating = switchMutation.isPending || selfAssignMutation.isPending || relieveMutation.isPending;

  const handlePositionClick = (action: ConfirmAction) => {
    if (currentPosition) {
      setConfirmTarget(action);
    } else {
      // Direct self-assign
      const data = action.type === 'ics'
        ? { ics_position_id: action.id }
        : { esf_id: action.id };
      selfAssignMutation.mutate({ incidentId, data }, {
        onSuccess: () => {
          toast.success('Position claimed', `You are now assigned as ${action.name}`);
          onClose();
        },
        onError: (error) => {
          toast.error('Failed to claim position', getErrorMessage(error, 'This position may already be filled'));
        },
      });
    }
  };

  const handleSwitch = (relieveCurrent: boolean) => {
    if (!confirmTarget) return;
    const data = {
      ...(confirmTarget.type === 'ics' ? { ics_position_id: confirmTarget.id } : { esf_id: confirmTarget.id }),
      relieve_current: relieveCurrent,
    };
    switchMutation.mutate({ incidentId, data }, {
      onSuccess: () => {
        const msg = relieveCurrent
          ? `You are now assigned as ${confirmTarget.name}`
          : `You are now also assigned as ${confirmTarget.name}`;
        toast.success(relieveCurrent ? 'Position switched' : 'Position added', msg);
        onClose();
      },
      onError: (error) => {
        toast.error('Failed to switch position', getErrorMessage(error, 'This position may already be filled'));
      },
    });
  };

  const handleRelieveOnly = () => {
    if (!currentPosition?.assignment_id) return;
    relieveMutation.mutate(currentPosition.assignment_id, {
      onSuccess: () => {
        toast.success('Position relieved', 'You have stepped down from your current position');
        onClose();
      },
      onError: () => {
        toast.error('Failed to relieve position');
      },
    });
  };

  // Split positions into qualified and other
  const qualifiedIcs = icsPositions.filter(p => p.is_active && qualifiedIcsIds.has(p.id));
  const otherIcs = icsPositions.filter(p => p.is_active && !qualifiedIcsIds.has(p.id));
  const qualifiedEsf = esfs.filter(e => e.is_active && qualifiedEsfIds.has(e.id));
  const otherEsf = esfs.filter(e => e.is_active && !qualifiedEsfIds.has(e.id));

  const hasQualified = qualifiedIcs.length > 0 || qualifiedEsf.length > 0;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={currentPosition ? 'Switch Position' : 'Claim Position'}
      size="lg"
      // Escape is handled below, where it backs out of the confirm step before
      // it closes the modal. Modal stops propagation on Escape, so letting it
      // close would silently discard that layering.
      closeOnEsc={false}
    >
      <>
        {/* Confirm overlay */}
        {confirmTarget && (
          <div className="px-5 py-4 border-b border-theme-primary bg-primary-50 dark:bg-gray-700">
            <p className="text-sm font-medium text-theme-primary mb-1">
              Switch to {confirmTarget.name}?
            </p>
            <p className="text-xs text-theme-secondary mb-3">
              You are currently assigned as {currentPosition?.position_name}. Choose how to proceed:
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                size="sm"
                disabled={isMutating}
                loading={switchMutation.isPending}
                onClick={() => handleSwitch(true)}
                className="text-xs"
              >
                Relieve & Switch
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={isMutating}
                onClick={() => handleSwitch(false)}
                className="text-xs"
              >
                Keep Both
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={isMutating}
                onClick={() => setConfirmTarget(null)}
                className="text-xs"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Current position info */}
          {currentPosition && !confirmTarget && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
              <div className="min-w-0">
                <p className="text-xs font-medium text-theme-tertiary uppercase tracking-wide">Current Position</p>
                <p className="heading-5 truncate">
                  {currentPosition.position_code && (
                    <span className="text-primary-600 dark:text-primary-400 mr-1">{currentPosition.position_code}</span>
                  )}
                  {currentPosition.position_name}
                </p>
                {currentPosition.esf_name && (
                  <p className="text-xs text-theme-tertiary">{currentPosition.esf_name}</p>
                )}
              </div>
              <Button
                variant="danger"
                size="sm"
                disabled={isMutating}
                loading={relieveMutation.isPending}
                onClick={handleRelieveOnly}
                className="shrink-0 ml-3 text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40"
              >
                Step Down
              </Button>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-gray-400">
              <FaIcon icon="spinner" spin className="h-5 w-5 mr-2" />
              Loading positions...
            </div>
          ) : (
            <>
              {/* Qualified Positions */}
              {hasQualified && (
                <div>
                  <h3 className="text-xs font-semibold text-theme-tertiary uppercase tracking-wide mb-2">
                    <FaIcon icon="star" className="h-3 w-3 mr-1 text-amber-500" />
                    Qualified Positions
                  </h3>
                  <div className="space-y-1 bg-amber-50/50 dark:bg-amber-900/10 rounded-lg p-2">
                    {qualifiedIcs.map(pos => (
                      <PositionRow
                        key={pos.id}
                        name={pos.name}
                        code={pos.code}
                        section={pos.section}
                        qualified
                        filledBy={filledIcsMap.get(pos.id)}
                        disabled={isMutating || filledIcsMap.has(pos.id)}
                        onClick={() => handlePositionClick({ type: 'ics', id: pos.id, name: pos.name })}
                      />
                    ))}
                    {qualifiedEsf.map(esf => (
                      <PositionRow
                        key={esf.id}
                        name={esf.name}
                        code={`ESF #${esf.number}`}
                        qualified
                        filledBy={filledEsfMap.get(esf.id)}
                        disabled={isMutating || filledEsfMap.has(esf.id)}
                        onClick={() => handlePositionClick({ type: 'esf', id: esf.id, name: esf.name })}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* All ICS Positions */}
              {otherIcs.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-theme-tertiary uppercase tracking-wide mb-2">
                    ICS Positions
                  </h3>
                  <div className="space-y-1">
                    {otherIcs.map(pos => (
                      <PositionRow
                        key={pos.id}
                        name={pos.name}
                        code={pos.code}
                        section={pos.section}
                        filledBy={filledIcsMap.get(pos.id)}
                        disabled={isMutating || filledIcsMap.has(pos.id)}
                        onClick={() => handlePositionClick({ type: 'ics', id: pos.id, name: pos.name })}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* All ESFs */}
              {otherEsf.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-theme-tertiary uppercase tracking-wide mb-2">
                    Emergency Support Functions
                  </h3>
                  <div className="space-y-1">
                    {otherEsf.map(esf => (
                      <PositionRow
                        key={esf.id}
                        name={esf.name}
                        code={`ESF #${esf.number}`}
                        filledBy={filledEsfMap.get(esf.id)}
                        disabled={isMutating || filledEsfMap.has(esf.id)}
                        onClick={() => handlePositionClick({ type: 'esf', id: esf.id, name: esf.name })}
                      />
                    ))}
                  </div>
                </div>
              )}

              {icsPositions.length === 0 && esfs.length === 0 && (
                <p className="text-sm text-theme-tertiary text-center py-4">
                  No positions available.
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-theme-primary flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </>
    </Modal>
  );
}

// Internal row component
function PositionRow({
  name,
  code,
  section,
  qualified,
  filledBy,
  disabled,
  onClick,
}: {
  name: string;
  code: string;
  section?: string;
  qualified?: boolean;
  filledBy?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors group ${
        filledBy
          ? 'opacity-60 cursor-not-allowed'
          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-theme-primary truncate">
          {name}
        </p>
        <p className="text-xs text-theme-tertiary">
          {code}
          {section && <span className="ml-1 capitalize"> - {section.replace('_', '/')}</span>}
        </p>
      </div>
      {filledBy ? (
        <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-surface-tertiary text-theme-tertiary">
          <FaIcon icon="user" className="h-2.5 w-2.5" />
          {filledBy}
        </span>
      ) : qualified ? (
        <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
          Qualified
        </span>
      ) : null}
      {!filledBy && (
        <FaIcon icon="chevron-right" className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
      )}
    </button>
  );
}
